// Utilidades para manejo de errores

export interface ErrorInfo {
  message: string
  code?: string
  status?: number
  isNetworkError?: boolean
  isTimeoutError?: boolean
  isAuthError?: boolean
  isRecoverable?: boolean
  retryCount?: number
  maxRetries?: number
}

/**
 * Analiza un error y retorna información estructurada
 */
export const analyzeError = (error: any): ErrorInfo => {
  const errorInfo: ErrorInfo = {
    message: "Error desconocido",
    isRecoverable: true,
    maxRetries: 3,
  }

  // Errores de red
  if (error.code === "ERR_NETWORK" || error.code === "NETWORK_ERROR") {
    errorInfo.message = "Error de conexión. Verifica tu conexión a internet."
    errorInfo.isNetworkError = true
    errorInfo.code = error.code
    errorInfo.isRecoverable = true
  }

  // Errores de timeout
  else if (error.code === "ECONNABORTED") {
    errorInfo.message = "La solicitud tardó demasiado. Intenta nuevamente."
    errorInfo.isTimeoutError = true
    errorInfo.code = error.code
    errorInfo.isRecoverable = true
  }

  // Errores HTTP
  else if (error.response) {
    errorInfo.status = error.response.status

    switch (error.response.status) {
      case 401:
        errorInfo.message = "No autorizado. Tu sesión ha expirado."
        errorInfo.isAuthError = true
        errorInfo.isRecoverable = false
        break
      case 403:
        errorInfo.message = "Acceso denegado"
        errorInfo.isAuthError = true
        errorInfo.isRecoverable = false
        break
      case 404:
        errorInfo.message = "Recurso no encontrado"
        errorInfo.isRecoverable = true
        break
      case 408:
        errorInfo.message = "Timeout de la solicitud. Intenta nuevamente."
        errorInfo.isTimeoutError = true
        errorInfo.isRecoverable = true
        break
      case 429:
        errorInfo.message = "Demasiadas solicitudes. Espera un momento."
        errorInfo.isRecoverable = true
        break
      case 500:
        errorInfo.message = "Error interno del servidor. Intenta más tarde."
        errorInfo.isRecoverable = false
        break
      case 502:
      case 503:
      case 504:
        errorInfo.message =
          "Servidor temporalmente no disponible. Intenta más tarde."
        errorInfo.isRecoverable = true
        break
      default:
        errorInfo.message = `Error ${error.response.status}`
        errorInfo.isRecoverable = error.response.status < 500
    }
  }

  // Errores de validación
  else if (error.message) {
    errorInfo.message = error.message
    errorInfo.isRecoverable = true
  }

  return errorInfo
}

/**
 * Determina si un error debe ser loggeado
 */
export const shouldLogError = (error: any, context?: string): boolean => {
  const errorInfo = analyzeError(error)

  // No loggear errores de red/tiempo en producción para evitar spam
  if (import.meta.env.MODE === "production") {
    if (errorInfo.isNetworkError || errorInfo.isTimeoutError) {
      return false
    }
  }

  return true
}

/**
 * Maneja errores de forma silenciosa (para errores no críticos)
 */
export const handleSilentError = (error: any, context?: string) => {
  if (shouldLogError(error, context)) {
    console.warn(`Silent error in ${context}:`, error)
  }

  // En producción, no hacer nada
}

/**
 * Determina si un error es recuperable
 */
export const isRecoverableError = (error: any): boolean => {
  const errorInfo = analyzeError(error)
  return errorInfo.isRecoverable || false
}

/**
 * Obtiene un mensaje de error amigable para el usuario
 */
export const getUserFriendlyMessage = (error: any): string => {
  const errorInfo = analyzeError(error)

  // Si es un error de red, dar consejos específicos
  if (errorInfo.isNetworkError) {
    return "Parece que hay un problema con tu conexión a internet. Verifica tu conexión y vuelve a intentar."
  }

  // Si es un timeout, sugerir reintentar
  if (errorInfo.isTimeoutError) {
    return "La solicitud está tardando más de lo esperado. Esto puede ser temporal. Intenta nuevamente."
  }

  // Para otros errores, usar el mensaje analizado
  return errorInfo.message
}

/**
 * Estrategia de reintentos para errores recuperables
 */
export const createRetryStrategy = (
  maxRetries: number = 3,
  baseDelay: number = 1000
) => {
  return {
    maxRetries,
    baseDelay,
    shouldRetry: (error: any, retryCount: number) => {
      if (retryCount >= maxRetries) return false
      return isRecoverableError(error)
    },
    getDelay: (retryCount: number) => {
      // Backoff exponencial con jitter
      const delay = baseDelay * Math.pow(2, retryCount)
      const jitter = Math.random() * 0.1 * delay
      return delay + jitter
    },
  }
}
